import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import lodash from "lodash";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  OrganizationProProfile,
  OrganizationProProfileStatus,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Alert from "components/Alert";
import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useAlert } from "utils/useAlert";

import ProOnboardingAboutForm from "./AboutForm";
import ProOnboardingCampaignForm from "./Campaigns";
import ProOnboardingCompleteScreen from "./CompleteScreen";
import ProOnboardingTemplatesForm from "./TemplatesForm";
import ProOnboardingWelcomeForm from "./Welcome";
import {
  DefaultProOnboardingSteps,
  ProOnboardingStep,
  StepToRoute,
} from "./schema";
import styles from "./styles";

const ProOnboarding = () => {
  const location = useLocation();
  const {
    currentOrg,
    setCurrentUser,
    setCurrentOrg,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    organizationProProfile,
    setOrganizationProProfile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const slideContext = useContext(SlideAnimationContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { slideProps } = useContext(SlideAnimationContext);
  const [message, severity, setAlert, closeAlert] = useAlert();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);

  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setOnboardingForm((prev) => ({
        ...prev,
        ...res.organizationProfile,
        name: res.organizationUser.name,
      }));

      setOrganizationProProfile(res.organizationProProfile);
      setProfile(res.organizationProfile);
      redirectToRightPage(res.organizationProProfile);
      slideContext.slideInUp();
    } catch (error) {}
  };

  const redirectToRightPage = (proProfile: OrganizationProProfile) => {
    if (
      proProfile?.status === OrganizationProProfileStatus.completed ||
      proProfile?.status === OrganizationProProfileStatus.later
    ) {
      navigate(`/${routes.home}`);
      return;
    }

    // User need to recomplete onboarding from start

    if (
      proProfile?.onboardingState?.steps &&
      !lodash.isEqual(
        proProfile.onboardingState.steps,
        DefaultProOnboardingSteps,
      )
    ) {
      trackProStep(ProOnboardingStep.welcome);
      navigate(`/${routes.onboardingProWelcome}${location.search}`);
      return;
    }

    const currentStep = Math.min(
      proProfile?.onboardingState?.currentStepIndex || 0,
      DefaultProOnboardingSteps?.length - 1,
    );
    const stepName = proProfile?.onboardingState?.steps[
      currentStep
    ] as ProOnboardingStep;

    if (stepName && stepName in StepToRoute) {
      const route = StepToRoute[stepName];
      trackProStep(stepName);
      navigate(`/${route}${location.search}`);
      return;
    }

    navigate(`/${routes.onboardingProWelcome}${location.search}`);
  };

  const finishSetUpLater = async () => {
    trackEvent("Pro Onboarding - Finish Set Up Later", {
      route: location.pathname,
    });

    navigate(`/${routes.home}`);
    updateProStatus(OrganizationProProfileStatus.later);
  };

  const updateProStatus = async (status: OrganizationProProfileStatus) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/pro-status`,
        "PATCH",
        {},
        {
          status,
        },
      );
      setOrganizationProProfile(res.organizationProProfile);
      navigate(`/${routes.home}`);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const handleNext = async (step: ProOnboardingStep) => {
    const indexOfStep = DefaultProOnboardingSteps.findIndex((s) => s === step);
    if (indexOfStep === -1) {
      return;
    }
    if (
      indexOfStep === DefaultProOnboardingSteps.length - 1 ||
      step === ProOnboardingStep.campaigns ||
      step === ProOnboardingStep.complete
    ) {
      navigate(`/${routes.home}`);
      if (
        organizationProProfile?.status !==
        OrganizationProProfileStatus.completed
      ) {
        await updateProStatus(OrganizationProProfileStatus.completed);
      }
    }
    const nextStepName = DefaultProOnboardingSteps[indexOfStep + 1];
    const nextPath = StepToRoute[nextStepName];
    navigate(`/${nextPath}${location.search}`);

    trackProStep(nextStepName);
  };

  const handleBack = (step: ProOnboardingStep) => {
    const indexOfStep = DefaultProOnboardingSteps.findIndex((s) => s === step);
    if (indexOfStep === -1 || indexOfStep === 0) {
      return;
    }
    if (indexOfStep === DefaultProOnboardingSteps.length - 1) {
      navigate(`/${routes.home}`);
    }

    const backStepName = DefaultProOnboardingSteps[indexOfStep - 1];
    const backPath = StepToRoute[backStepName];
    navigate(`/${backPath}${location.search}`);

    trackProStep(backStepName);
  };

  const trackProStep = async (step: ProOnboardingStep) => {
    setNextLoading(true);
    try {
      const { organizationProProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/track-pro-step`,
        "POST",
        {},
        {
          page: location.pathname,
          source: getSource(),
          currentStepName: step,
          steps: DefaultProOnboardingSteps,
        },
      );
      if (organizationProProfile) {
        setOrganizationProProfile(organizationProProfile);
      }
      setNextLoading(false);
    } catch (error) {
      setNextLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid
            sx={[
              styles.formContainer,
              {
                maxWidth: {
                  md: isPage(routes.templatesSelfServeOnboarding)
                    ? "2400px"
                    : "1200px",
                },
              },
            ]}
            item
            xs={12}
          >
            <Routes>
              <Route
                path="*"
                element={
                  <Navigate to={`/${routes.onboardingProWelcome}`} replace />
                }
              />

              <Route
                path="welcome/*"
                element={
                  <ProOnboardingWelcomeForm
                    handleNext={() => handleNext(ProOnboardingStep.welcome)}
                    finishSetUpLater={finishSetUpLater}
                  />
                }
              />

              <Route
                path="about/*"
                element={
                  <ProOnboardingAboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleBack={() => handleBack(ProOnboardingStep.about)}
                    handleNext={() => handleNext(ProOnboardingStep.about)}
                    onFinishSetUpLater={finishSetUpLater}
                  />
                }
              />

              <Route
                path="templates/*"
                element={
                  <ProOnboardingTemplatesForm
                    handleNext={() => handleNext(ProOnboardingStep.templates)}
                    handleBack={() => handleBack(ProOnboardingStep.templates)}
                    onFinishSetUpLater={finishSetUpLater}
                  />
                }
              />
              <Route
                path="campaigns/*"
                element={
                  <ProOnboardingCampaignForm
                    handleNext={() => handleNext(ProOnboardingStep.campaigns)}
                    handleBack={() => handleBack(ProOnboardingStep.campaigns)}
                    onFinishSetUpLater={finishSetUpLater}
                  />
                }
              />
              <Route
                path="complete/*"
                element={
                  <ProOnboardingCompleteScreen
                    handleNext={() => handleNext(ProOnboardingStep.complete)}
                    handleBack={() => handleBack(ProOnboardingStep.complete)}
                    nextLoading={nextLoading}
                  />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>

      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </>
  );
};

export default function ProOnboardingWrapper() {
  return (
    <SlideAnimationProvider>
      <ProOnboarding />
    </SlideAnimationProvider>
  );
}
